import { useState } from 'react';
import { api, type Finding } from '../api/client';
import { Async } from '../components/Async';
import { ConnectorDowngrade } from '../components/ConnectorDowngrade';
import { CoreIntegrations } from '../components/CoreIntegrations';
import { EdgeControl } from '../components/EdgeControl';
import { EventReviewDetails } from '../components/EventReviewDetails';
import { SiteDiagnosis } from '../components/SiteDiagnosis';
import { WpModeControl } from '../components/WpModeControl';
import { describeFimCoverage } from '../lib/fim-coverage';
import { isOutdated } from '../lib/version';
import { useFetch } from '../useFetch';

/**
 * Human labels for scan signatures. The signature is a stable machine key (the finding lifecycle
 * dedupes on it), so it must never be shown raw — `missing_x_content_type_options` is not a sentence.
 */
const FINDING_LABELS: Record<string, string> = {
  unreachable: 'Site unreachable',
  server_error: 'Server error',
  client_error: 'Error response',
  // Deliberately not phrased as a fault: an outside probe cannot tell "refused us" from "broken",
  // and in the field this is usually bot protection on a perfectly healthy site (ADR-0018).
  scan_blocked: 'Couldn’t assess — the site refused our scanner',
  slow_response: 'Slow response',
  missing_hsts: 'No HSTS (Strict-Transport-Security)',
  missing_csp: 'No Content-Security-Policy',
  missing_x_content_type_options: 'No X-Content-Type-Options',
  missing_x_frame_options: 'No X-Frame-Options',
};

/**
 * Integrity signatures are DYNAMIC — `modified:<path>`, `package_unexpected:plugin:<slug>` — so they
 * cannot be a lookup table like the remote-scan ones. Parse them into something a human reads.
 */
function describeFinding(f: Finding): { title: string; detail: string; rollup?: RollupRef } {
  const e = (f.evidence ?? {}) as Record<string, unknown>;

  // Rolled up: one row standing in for many files in a package (ADR-0018). The count is the point.
  const pkgMatch = /^package_(modified|unexpected):(.+)$/.exec(f.signature);
  if (pkgMatch) {
    const [, kind, pkg] = pkgMatch;
    const count = typeof e.fileCount === 'number' ? e.fileCount : 0;
    const name = (pkg ?? '')
      .replace(/^plugin:/, '')
      .replace(/^theme:/, '')
      .replace(/^core:core$/, 'WordPress core');
    return {
      title:
        kind === 'modified'
          ? `${name} — ${count} files don’t match the published version`
          : `${name} — ${count} files aren’t in the published version`,
      detail: 'Grouped: one entry for the whole package. Show the files to see which.',
      rollup: { pkg: pkg ?? '', kind: kind as 'modified' | 'unexpected', count },
    };
  }

  const fileMatch = /^(modified|unexpected):(.+)$/.exec(f.signature);
  if (fileMatch) {
    const [, kind, path] = fileMatch;
    const pkg = typeof e.package === 'string' ? e.package : '';
    return {
      title: kind === 'modified' ? 'File does not match the published version' : 'Unexpected file',
      detail: `${path}${pkg ? ` · ${pkg}` : ''}`,
    };
  }

  // Remote-scan findings keep their fixed labels and their one useful fact.
  if (typeof e.status === 'number') return { title: labelFor(f), detail: `HTTP ${e.status}` };
  if (typeof e.ms === 'number') return { title: labelFor(f), detail: `${Math.round(e.ms)} ms` };
  if (typeof e.error === 'string') return { title: labelFor(f), detail: e.error };
  return { title: labelFor(f), detail: f.path ?? '' };
}

type RollupRef = { pkg: string; kind: 'modified' | 'unexpected'; count: number };

const labelFor = (f: Finding) => FINDING_LABELS[f.signature] ?? f.signature;

/** The files behind a rolled-up finding, fetched only when the reader asks for them. */
function PackageFiles({ siteId, pkgRef }: { siteId: string; pkgRef: RollupRef }) {
  const diff = useFetch(
    () => api.packageDiff(siteId, pkgRef.pkg, pkgRef.kind),
    `${siteId}:${pkgRef.pkg}:${pkgRef.kind}`,
  );
  return (
    <Async state={diff}>
      {(data) => (
        <div className="drilldown">
          <p className="muted">
            {data.version ? `Compared against ${data.package} ${data.version}. ` : ''}
            {data.files.length} file{data.files.length === 1 ? '' : 's'} shown
            {data.truncated && ' (capped — there are more)'}.
          </p>
          <ul className="pathlist">
            {data.files.map((file) => (
              <li key={file.path}>
                <code>{file.path}</code>
                <span className="muted">
                  {' '}
                  · first seen {new Date(file.firstSeenAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Async>
  );
}

/** One site: its enforcement mode (togglable by owner/admin), recent edge decisions, inventory. */
export function SiteDetail({
  siteId,
  canEdit,
  canAddProtection,
}: {
  siteId: string;
  canEdit: boolean;
  canAddProtection: boolean;
}) {
  const [expandedFinding, setExpandedFinding] = useState<string | null>(null);
  const siteState = useFetch(() => api.site(siteId), siteId);
  const protection = useFetch(() => api.protection(siteId), siteId);
  const findings = useFetch(() => api.findings(siteId), siteId);
  const events = useFetch(() => api.events(siteId), siteId);
  const inventory = useFetch(() => api.inventory(siteId), siteId);
  // Latest published connector release (null = none / unavailable — no badges).
  const connector = useFetch(() => api.connectorInfo(), siteId);
  const commands = useFetch(() => api.siteCommands(siteId), siteId);
  const [cmdNote, setCmdNote] = useState<string | null>(null);

  async function removeSite(url: string, onEdge: boolean) {
    // ADR-0025 §4: for a site on the hosted edge, "enforcement stops" is true and dangerously
    // incomplete — its traffic keeps flowing through our Worker in bypass until an OPERATOR removes
    // the route, which no console action can do. Say that rather than imply a clean break.
    const edgeNote = onEdge
      ? " Traffic keeps flowing through the Guard Dog edge in bypass — passed straight to your origin, unfiltered — until an operator removes the site's edge route."
      : '';
    if (
      !window.confirm(
        `Remove ${url} from the fleet? Its agent credentials are revoked and edge enforcement stops.${edgeNote} The site's plugin keeps running standalone.`,
      )
    ) {
      return;
    }
    try {
      await api.deleteSite(siteId);
      window.location.hash = '#/';
    } catch {
      setCmdNote('Could not remove the site.');
    }
  }

  async function requestInventory() {
    setCmdNote(null);
    try {
      await api.enqueueCommand(siteId, 'inventory.report');
      setCmdNote('Queued — the site picks it up on its next sync (≤5 min).');
      commands.reload();
    } catch {
      setCmdNote('Could not queue the command.');
    }
  }

  return (
    <section>
      <p>
        <a href="#/">← Fleet</a>
      </p>

      <Async state={siteState}>
        {({ site }) => (
          <>
            <div className="section-head">
              <h2>{site.url}</h2>
              {canEdit && (
                <button
                  type="button"
                  className="copy-btn danger"
                  onClick={() => void removeSite(site.url, Boolean(site.edgeHostname))}
                >
                  Remove site
                </button>
              )}
            </div>
            <div className="row">
              <span className="label">Edge mode</span>
              <EdgeControl
                site={site}
                canEdit={canEdit}
                canAddProtection={canAddProtection}
                onChanged={siteState.reload}
              />
            </div>
            {/* Two planes, two failure modes, and they are allowed to disagree — a site enforcing at
                the edge while monitoring in WordPress is the sane rollout order (ADR-0030). */}
            <div className="row">
              <span className="label">WordPress mode</span>
              <WpModeControl
                site={site}
                canEdit={canEdit}
                canAddProtection={canAddProtection}
                onChanged={siteState.reload}
              />
            </div>
            <ConnectorDowngrade siteId={site.id} />
            <CoreIntegrations seams={site.seams} />
            <div className="row">
              {/* Always offered here, unlike the Fleet list where it appears only once a site has
                  gone quiet. A control reachable only during an incident is one nobody has used
                  before the incident — undiscoverable, unverifiable, and first met under stress.
                  On the site's own page there is room to offer it unconditionally. */}
              <span className="label">Reporting</span>
              <SiteDiagnosis siteId={site.id} />
            </div>
          </>
        )}
      </Async>

      {/* Platform protection — the operator-side mirror of the two wp-admin annotations. */}
      <h3>Platform protection</h3>
      <Async state={protection}>
        {(p) => (
          <>
            <div className="row">
              <span className="label">Edge activity</span>
              <span>
                {p.edge === null ? (
                  <span className="muted">Not on the hosted edge.</span>
                ) : p.edge.total === 0 ? (
                  <span className="muted">
                    No edge activity in the last {p.edge.windowDays} days.
                  </span>
                ) : (
                  <>
                    {p.edge.mode === 'enforce' ? 'Blocked' : 'Flagged'} {p.edge.total} request
                    {p.edge.total === 1 ? '' : 's'} in the last {p.edge.windowDays} days
                    {p.edge.mode !== 'enforce' && <span className="muted"> (monitor)</span>}
                    {Object.keys(p.edge.byType).length > 0 && (
                      <span className="muted">
                        {' — '}
                        {Object.entries(p.edge.byType)
                          .map(([t, n]) => `${t}: ${n}`)
                          .join(', ')}
                      </span>
                    )}
                  </>
                )}
              </span>
            </div>
            <div className="row">
              <span className="label">Virtual patches</span>
              <span>
                {p.vpatch === null ? (
                  <span className="muted">
                    Awaiting inventory — no coverage claim until the site reports.
                  </span>
                ) : p.vpatch.vulnerabilities === 0 ? (
                  <span className="muted">No known vulnerabilities in installed software.</span>
                ) : (
                  <>
                    Shielding {p.vpatch.vulnerabilities} known vulnerabilit
                    {p.vpatch.vulnerabilities === 1 ? 'y' : 'ies'}
                    {p.vpatch.cves.length > 0 && (
                      <span className="muted"> — {p.vpatch.cves.join(', ')}</span>
                    )}
                  </>
                )}
              </span>
            </div>
          </>
        )}
      </Async>

      {/* Remote probe (Stage A) + file integrity (Stage B) — ADR-0018. */}
      <h3>Scan findings</h3>
      <Async state={findings}>
        {(data) => (
          <>
            <p className="muted">
              {data.latestScan === null
                ? 'Not scanned yet — the first pass runs within 15 minutes.'
                : `Last probed ${new Date(data.latestScan.at).toLocaleString()}`}
              {data.latestFimScan &&
                ` · files checked ${new Date(data.latestFimScan.at).toLocaleString()}`}
              {data.count === 0 && ' — nothing to report.'}
            </p>
            {/* A truncated walk means we did NOT see the whole install, and silence would otherwise
                read as safety. Say so plainly rather than letting a partial picture look complete. */}
            {data.fimScopes.some((s) => s.truncated) && (
              <p className="warnbar">
                Some of this site’s files could not be fully checked, so this list may be
                incomplete.
              </p>
            )}
            {data.fimScopes.length > 0 && (
              <p className="muted">{describeFimCoverage(data.fimScopes)}</p>
            )}
            {data.count > 0 && (
              <table>
                <thead>
                  <tr>
                    <th>Severity</th>
                    <th>Finding</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {data.findings.map((f) => {
                    const d = describeFinding(f);
                    const isOpen = expandedFinding === f.id;
                    return (
                      <tr key={f.id}>
                        <td>
                          <span className={`sev sev-${f.severity}`}>{f.severity}</span>
                        </td>
                        <td>
                          {d.title}
                          <span className="muted"> · {f.category}</span>
                          {d.rollup && (
                            <>
                              {' '}
                              <button
                                type="button"
                                className="linkbtn"
                                onClick={() => setExpandedFinding(isOpen ? null : f.id)}
                              >
                                {isOpen ? 'Hide files' : 'Show files'}
                              </button>
                              {isOpen && <PackageFiles siteId={siteId} pkgRef={d.rollup} />}
                            </>
                          )}
                        </td>
                        <td className="muted">{d.detail}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}
      </Async>

      <h3>Recent events</h3>
      <Async state={events}>
        {(data) => (
          <>
            <p className="muted">
              {data.count} events
              {Object.keys(data.byType).length > 0 && (
                <>
                  {' — '}
                  {Object.entries(data.byType)
                    .map(([t, n]) => `${t}: ${n}`)
                    .join(', ')}
                </>
              )}
            </p>
            {data.events.length > 0 && (
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Type</th>
                    <th>Severity</th>
                    <th>Request &amp; decision</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.map((e, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: events have no stable client id here.
                    <tr key={i}>
                      <td className="muted">{e.ts}</td>
                      <td>{e.type}</td>
                      <td>{e.severity}</td>
                      <td>
                        <EventReviewDetails event={e} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </Async>

      <div className="section-head">
        <h3>Inventory</h3>
        {canEdit && (
          <button type="button" className="copy-btn" onClick={() => void requestInventory()}>
            Refresh inventory
          </button>
        )}
      </div>
      {cmdNote && <p className="muted">{cmdNote}</p>}
      <Async state={inventory}>
        {(data) =>
          data.count === 0 ? (
            <p className="muted">No inventory reported.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Package</th>
                  <th>Type</th>
                  <th>Version</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => {
                  // Badge the connector row when a newer release is published (self-update pending).
                  const outdated =
                    it.slug === 'guard-dog-connector' &&
                    isOutdated(it.version, connector.data?.latestVersion);
                  return (
                    <tr key={`${it.packageType}:${it.slug}`}>
                      <td>{it.slug}</td>
                      <td className="muted">{it.packageType}</td>
                      <td>
                        {it.version}
                        {outdated && (
                          <span
                            className="badge badge-monitor badge-update"
                            title={`v${connector.data?.latestVersion} is published`}
                          >
                            update available
                          </span>
                        )}
                      </td>
                      <td>{it.active ? 'yes' : 'no'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        }
      </Async>

      {/* Command lifecycle (Channel B): queued → delivered (next sync) → acked by the plugin. */}
      {commands.data && commands.data.count > 0 && (
        <>
          <h3>Recent commands</h3>
          <table>
            <thead>
              <tr>
                <th>Command</th>
                <th>Status</th>
                <th>Queued</th>
                <th>Acked</th>
              </tr>
            </thead>
            <tbody>
              {commands.data.commands.map((cmd) => (
                <tr key={cmd.id}>
                  <td className="mono">{cmd.verb}</td>
                  <td>
                    <span className={`badge badge-cmd-${cmd.status}`}>{cmd.status}</span>
                    {cmd.detail && <span className="muted"> — {cmd.detail}</span>}
                  </td>
                  <td className="muted">{cmd.createdAt}</td>
                  <td className="muted">{cmd.ackedAt ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
